import React, { useEffect, useState, useMemo } from 'react';
import api from '../api';
import DrillDownModal from '../components/DrillDownModal';
import { ChartBar, Lightning, ShoppingCart, Shield, Target, CurrencyCircleDollar, Eye, Warning, TrendUp, TrendDown, ArrowRight, Clock, Rewind, FastForward, Play, Pause } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LineChart, Line, Legend, ReferenceLine } from 'recharts';

const OUTCOME_COLORS = ['#0A4D3C', '#C2714F', '#D9943B', '#87A98E'];

function ScoreGauge({ label, score, color, prevScore, index, onClick }) {
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const delta = prevScore != null ? score - prevScore : null;
  return (
    <div data-testid={`outcome-${index}`} onClick={onClick} className="bg-white border border-border-default rounded-lg p-4 flex flex-col items-center hover:-translate-y-0.5 hover:shadow-sm transition-all cursor-pointer group">
      <svg width="100" height="100" className="mb-2">
        <circle cx="50" cy="50" r={radius} fill="none" stroke="#F1F3F5" strokeWidth="7" />
        <circle cx="50" cy="50" r={radius} fill="none" stroke={color} strokeWidth="7" strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round" transform="rotate(-90 50 50)" className="transition-all duration-700" />
        <text x="50" y="46" textAnchor="middle" dominantBaseline="central" className="font-heading font-bold text-xl" fill="#1A1F1B">{score}</text>
        {delta != null && (
          <text x="50" y="64" textAnchor="middle" dominantBaseline="central" fill={delta > 0 ? '#0A4D3C' : delta < 0 ? '#C6493A' : '#6B7C6E'} fontSize="10" fontWeight="600">
            {delta > 0 ? `+${delta}` : delta === 0 ? '—' : delta}
          </text>
        )}
      </svg>
      <p className="text-[11px] text-content-secondary text-center font-medium leading-tight">{label}</p>
      <p className="text-[8px] text-brand-accent opacity-0 group-hover:opacity-100 transition-opacity mt-1">Click to drill down</p>
    </div>
  );
}

function TimeMachineSlider({ quarters, selectedIdx, onChange, isPlaying, onTogglePlay }) {
  if (!quarters.length) return null;
  return (
    <div data-testid="time-machine" className="bg-white border-2 border-brand-accent/20 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Clock size={16} weight="duotone" className="text-brand-accent" />
          <span className="font-heading font-semibold text-sm text-content-primary">Time Machine</span>
          <span className="text-[10px] text-content-disabled ml-1">Drag to travel through quarters</span>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="time-machine-rewind" onClick={() => onChange(0)} className="p-1 rounded hover:bg-surface-muted transition-colors" title="Go to start">
            <Rewind size={14} className="text-content-secondary" />
          </button>
          <button data-testid="time-machine-play" onClick={onTogglePlay} className="p-1.5 rounded-full bg-brand-accent text-white hover:opacity-90 transition-opacity" title={isPlaying ? 'Pause' : 'Play'}>
            {isPlaying ? <Pause size={12} weight="fill" /> : <Play size={12} weight="fill" />}
          </button>
          <button data-testid="time-machine-forward" onClick={() => onChange(quarters.length - 1)} className="p-1 rounded hover:bg-surface-muted transition-colors" title="Go to end">
            <FastForward size={14} className="text-content-secondary" />
          </button>
          <span data-testid="time-machine-period" className="ml-2 px-3 py-1 bg-brand-accent/10 rounded-full text-xs font-heading font-bold text-brand-accent">
            {quarters[selectedIdx]?.period}
          </span>
        </div>
      </div>
      <div className="relative px-1">
        <input
          data-testid="time-machine-slider"
          type="range" min={0} max={quarters.length - 1} value={selectedIdx}
          onChange={e => onChange(parseInt(e.target.value))}
          className="w-full h-2 rounded-full appearance-none cursor-pointer accent-brand-accent"
          style={{
            background: `linear-gradient(to right, #C2714F 0%, #C2714F ${(selectedIdx / (quarters.length - 1)) * 100}%, #E8ECE9 ${(selectedIdx / (quarters.length - 1)) * 100}%, #E8ECE9 100%)`
          }}
        />
        <div className="flex justify-between mt-1.5">
          {quarters.map((q, i) => (
            <button key={i} onClick={() => onChange(i)}
              className={`text-[8px] font-mono transition-all ${i === selectedIdx ? 'text-brand-accent font-bold scale-110' : 'text-content-disabled hover:text-content-secondary'}`}>
              {q.period.replace('20', "'").replace(' ', '')}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ExecutiveCockpit() {
  const [timelineData, setTimelineData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedIdx, setSelectedIdx] = useState(11);
  const [isPlaying, setIsPlaying] = useState(false);
  const [drillKey, setDrillKey] = useState(null);
  const [facilitiesData, setFacilitiesData] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get('/api/dashboard/executive/timeline'),
      api.get('/api/dashboard/executive'),
    ]).then(([tl, ex]) => {
      setTimelineData(tl.data);
      setFacilitiesData(ex.data?.facilities_data || []);
      setSelectedIdx((tl.data?.quarters?.length || 1) - 1);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // Auto-play
  useEffect(() => {
    if (!isPlaying || !timelineData) return;
    const interval = setInterval(() => {
      setSelectedIdx(prev => {
        if (prev >= (timelineData.quarters?.length || 1) - 1) {
          setIsPlaying(false);
          return prev;
        }
        return prev + 1;
      });
    }, 1200);
    return () => clearInterval(interval);
  }, [isPlaying, timelineData]);

  const quarters = timelineData?.quarters || [];
  const currentQ = quarters[selectedIdx] || {};
  const prevQ = selectedIdx > 0 ? quarters[selectedIdx - 1] : null;
  const s = currentQ.summary || {};
  const outcomes = currentQ.outcomes || {};

  // Full outcome trend for chart
  const outcomeTrend = useMemo(() => {
    return quarters.map(q => ({
      period: q.period,
      'Financial Sector': q.outcomes?.outcome_1 || 0,
      'Monetary Policy': q.outcomes?.outcome_2 || 0,
      'Financing': q.outcomes?.outcome_3 || 0,
      'Own Ops': q.outcomes?.outcome_4 || 0,
    }));
  }, [quarters]);

  // HO Electricity trend
  const hoElec = useMemo(() => {
    return facilitiesData
      ?.filter(d => d.site_id === 'BSP-HO-MNL' && d.metric_id === 'FAC-001')
      .sort((a, b) => a.period?.localeCompare(b.period)) || [];
  }, [facilitiesData]);

  if (loading) return <div className="animate-pulse text-content-secondary">Loading executive cockpit...</div>;
  if (!timelineData) return <div className="text-status-alert">Failed to load dashboard</div>;

  return (
    <div data-testid="executive-cockpit" className="space-y-5 animate-fade-in">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading font-light text-3xl text-content-primary tracking-tight">Executive Sustainability Cockpit</h1>
          <p className="text-sm text-content-secondary mt-1">BSP four-outcome performance view</p>
        </div>
        <span className="text-xs text-content-disabled font-mono">{currentQ.period || new Date().toLocaleDateString('en-PH', { year: 'numeric', month: 'long' })}</span>
      </div>

      {/* Time Machine */}
      <TimeMachineSlider
        quarters={quarters}
        selectedIdx={selectedIdx}
        onChange={setSelectedIdx}
        isPlaying={isPlaying}
        onTogglePlay={() => setIsPlaying(!isPlaying)}
      />

      {/* 4 Outcome Scores */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <ScoreGauge label="Financial-Sector Internalization" score={outcomes.outcome_1 || 0} prevScore={prevQ?.outcomes?.outcome_1} color={OUTCOME_COLORS[0]} index={1} onClick={() => setDrillKey('exec_outcome_1')} />
        <ScoreGauge label="Monetary Policy Integration" score={outcomes.outcome_2 || 0} prevScore={prevQ?.outcomes?.outcome_2} color={OUTCOME_COLORS[1]} index={2} onClick={() => setDrillKey('exec_outcome_2')} />
        <ScoreGauge label="Financing Solutions" score={outcomes.outcome_3 || 0} prevScore={prevQ?.outcomes?.outcome_3} color={OUTCOME_COLORS[2]} index={3} onClick={() => setDrillKey('exec_outcome_3')} />
        <ScoreGauge label="Own Operations" score={outcomes.outcome_4 || 0} prevScore={prevQ?.outcomes?.outcome_4} color={OUTCOME_COLORS[3]} index={4} onClick={() => setDrillKey('exec_outcome_4')} />
      </div>

      {/* KPI Summary Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { icon: Target, label: 'Green Finance', val: s.green_finance_approved || 0, sub: `of ${s.green_finance_total || 0} approved`, color: 'text-brand-primary', dk: 'exec_green_finance' },
          { icon: Eye, label: 'Alerts', val: s.total_alerts || 0, sub: `${s.open_alerts || 0} open`, color: (s.open_alerts || 0) > 0 ? 'text-status-alert' : 'text-status-on-track', dk: 'exec_high_risk_bsfi' },
          { icon: CurrencyCircleDollar, label: 'Anomalies', val: s.anomalies_detected || 0, sub: `in ${s.metric_readings || 0} readings`, color: (s.anomalies_detected || 0) > 3 ? 'text-status-alert' : 'text-status-watch', dk: 'exec_anomalies' },
          { icon: ShoppingCart, label: 'Green Procurement', val: `${s.procurement_green_pct || 0}%`, sub: `${s.procurement_green || 0}/${s.procurement_total || 0}`, color: 'text-status-on-track', dk: 'exec_green_procurement' },
          { icon: Shield, label: 'Overdue Actions', val: s.governance_overdue || 0, sub: `reporting ${s.reporting_pct || 0}% approved`, color: (s.governance_overdue || 0) > 0 ? 'text-status-alert' : 'text-status-on-track', dk: 'exec_overdue_actions' },
        ].map((kpi, i) => (
          <div key={i} data-testid={`kpi-${i}`} onClick={() => setDrillKey(kpi.dk)} className="bg-white border border-border-default rounded-lg p-3.5 hover:-translate-y-0.5 hover:shadow-sm transition-all cursor-pointer group">
            <kpi.icon size={16} className={`${kpi.color} mb-1.5`} />
            <p className={`text-xl font-heading font-bold ${kpi.color}`}>{kpi.val}</p>
            <p className="text-[10px] text-content-secondary font-medium">{kpi.label}</p>
            <p className="text-[9px] text-content-disabled">{kpi.sub}</p>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Outcome Trend with marker */}
        <div className="lg:col-span-2 bg-white border border-border-default rounded-lg p-4">
          <h3 className="font-heading font-semibold text-sm text-content-primary mb-3">Outcome Score Trends — 3 Year View</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={outcomeTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F3F5" />
              <XAxis dataKey="period" tick={{ fontSize: 9, fill: '#49564C' }} interval={0} angle={-25} textAnchor="end" height={40} />
              <YAxis domain={[20, 100]} tick={{ fontSize: 10, fill: '#49564C' }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              {currentQ.period && <ReferenceLine x={currentQ.period} stroke="#C2714F" strokeWidth={2} strokeDasharray="4 2" label={{ value: "Now", position: "top", fontSize: 9, fill: "#C2714F" }} />}
              <Line type="monotone" dataKey="Financial Sector" stroke="#0A4D3C" strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 5 }} />
              <Line type="monotone" dataKey="Monetary Policy" stroke="#C2714F" strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 5 }} />
              <Line type="monotone" dataKey="Financing" stroke="#D9943B" strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 5 }} />
              <Line type="monotone" dataKey="Own Ops" stroke="#87A98E" strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Red Flags for current quarter */}
        <div className="bg-white border border-border-default rounded-lg p-4">
          <h3 className="font-heading font-semibold text-sm text-content-primary mb-3">
            Red Flags & Alerts
            <span className="text-[9px] text-content-disabled font-normal ml-2">{currentQ.period}</span>
          </h3>
          <div className="space-y-2 max-h-[210px] overflow-y-auto">
            {currentQ.red_flag && currentQ.red_flag !== 'None' && (
              <div className="p-2.5 bg-status-alert-bg/50 rounded-lg border border-status-alert/10">
                <p className="text-[10px] font-mono text-brand-primary font-medium">{currentQ.period}</p>
                <p className="text-xs text-content-primary mt-0.5">{currentQ.red_flag}</p>
                <p className="text-[10px] text-brand-accent mt-1 font-medium">{currentQ.recommended_action}</p>
              </div>
            )}
            {(currentQ.alerts || []).map((a, i) => (
              <div key={i} className="flex items-start gap-2 p-2 bg-surface-default rounded-lg">
                <Warning size={12} weight="fill" className={a.severity === 'High' ? 'text-status-alert mt-0.5' : 'text-status-watch mt-0.5'} />
                <div>
                  <p className="text-[10px] text-content-primary">{a.trigger}</p>
                  <p className="text-[9px] text-content-disabled">{a.site} · {a.status}</p>
                </div>
              </div>
            ))}
            {(!currentQ.alerts || currentQ.alerts.length === 0) && (!currentQ.red_flag || currentQ.red_flag === 'None') && (
              <div className="p-2.5 bg-emerald-50/50 rounded-lg border border-emerald-100">
                <p className="text-[10px] text-emerald-700 font-medium">No red flags this quarter</p>
                {currentQ.recommended_action && <p className="text-[10px] text-content-secondary mt-1">{currentQ.recommended_action}</p>}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* HO Electricity Trend */}
      {hoElec.length > 0 && (
        <div className="bg-white border border-border-default rounded-lg p-4">
          <h3 className="font-heading font-semibold text-sm text-content-primary mb-3">Head Office Electricity (kWh) — Full History</h3>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={hoElec}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F3F5" />
              <XAxis dataKey="period" tick={{ fontSize: 8, fill: '#49564C' }} interval={2} />
              <YAxis tick={{ fontSize: 10, fill: '#49564C' }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={v => [v.toLocaleString() + ' kWh']} />
              <Bar dataKey="value" radius={[2, 2, 0, 0]}>
                {hoElec.map((e, i) => <Cell key={i} fill={e.anomaly_flag ? '#C6493A' : e.status === 'Alert' ? '#C6493A' : e.status === 'Watch' ? '#D9943B' : '#0A4D3C'} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      <DrillDownModal isOpen={!!drillKey} onClose={() => setDrillKey(null)} metricKey={drillKey} />
    </div>
  );
}
